// Email tracking: connect your own Gmail or Outlook mailbox, and see which
// mailboxes this workspace reads (backend: server/mail/mailTracking.js,
// GET /api/mail-tracking/status, POST /api/mail-tracking/disconnect).
// Each person connects only their own mailbox; an admin sees every mailbox
// and can stop reading one.
import React from "react";

const PROVIDER_LABEL = { google: "Gmail", microsoft: "Outlook" };

function lower(v) {
  return String(v || "")
    .trim()
    .toLowerCase();
}

function ago(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const min = Math.round((Date.now() - t) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function watchPopup(pop, onDone) {
  if (!pop) {
    onDone();
    return;
  }
  const t = setInterval(() => {
    if (!pop || pop.closed) {
      clearInterval(t);
      onDone();
    }
  }, 1000);
  setTimeout(() => clearInterval(t), 10 * 60 * 1000);
}

function readLine(m) {
  if (m.error) return m.error;
  if (!m.lastReadAt) return "Connected. Read on the next run.";
  const n = Number(m.lastThreadCount) || 0;
  return `Read ${ago(m.lastReadAt)}: ${n} matching thread${n === 1 ? "" : "s"}.`;
}

export default function EmailTrackingMailboxes({ apiBase, currentUserEmail, canManageWorkspace }) {
  const base = String(apiBase || "").replace(/\/+$/, "");
  const me = lower(currentUserEmail);
  const [status, setStatus] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [busy, setBusy] = React.useState("");

  const refresh = React.useCallback(async () => {
    if (!base) return;
    try {
      const r = await fetch(`${base}/api/mail-tracking/status`, { credentials: "include" });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j?.ok) throw new Error(j?.error || "status_failed");
      setStatus(j);
      setLoadError("");
    } catch {
      setLoadError("Couldn't load email tracking status. Refresh the page to try again.");
    }
  }, [base]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Signing in finishes in another window, and Google's page hides that window's state from
  // this one (see lib/signInPopup.js), so re-read on focus too.
  React.useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  function connect(provider) {
    if (!me || busy) return;
    setBusy(provider);
    const url =
      provider === "microsoft"
        ? `${base}/api/ms/oauth/start?feature=mail&email=${encodeURIComponent(me)}`
        : `${base}/api/google/oauth/start?mode=mail&email=${encodeURIComponent(me)}`;
    let pop = null;
    try {
      pop = window.open(url, `bossdrop-mail-${provider}`, "width=620,height=720");
    } catch {}
    watchPopup(pop, () => {
      setBusy("");
      refresh();
    });
  }

  async function stop(m) {
    if (busy) return;
    setBusy(`stop:${m.provider}:${m.email}`);
    try {
      await fetch(`${base}/api/mail-tracking/disconnect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: m.email, provider: m.provider }),
      });
    } catch {}
    setBusy("");
    refresh();
  }

  const you = status?.you || {};
  const mailboxes = Array.isArray(status?.mailboxes) ? status.mailboxes : [];
  const myMailboxes = mailboxes.filter((m) => lower(m.email) === me);
  const others = mailboxes.filter((m) => lower(m.email) !== me);
  const gmailOn = !!you?.google?.mail;
  const outlookOn = !!you?.microsoft?.mail;

  return (
    <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
      <p className="muted" style={{ margin: 0 }}>
        BossDrop reads email threads from the mailboxes people connect here and
        keeps only the threads that involve your team or the domains below.
        Each person connects their own mailbox. Nothing is read until they do.
      </p>

      {loadError ? <div className="muted small">{loadError}</div> : null}

      <div className="panelSub" style={{ margin: 0, padding: 12, display: "grid", gap: 8 }}>
        <strong>Your mailbox{me ? ` (${me})` : ""}</strong>
        {myMailboxes.length ? (
          myMailboxes.map((m) => (
            <div
              key={`${m.provider}:${m.email}`}
              style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
            >
              <span className={`chip ${m.error ? "warn" : "ok"}`}>
                {PROVIDER_LABEL[m.provider] || m.provider}: {m.error ? "needs attention" : "reading"}
              </span>
              <span className="muted small" style={{ flex: "1 1 220px" }}>
                {readLine(m)}
              </span>
              <button
                type="button"
                className="btn"
                disabled={!!busy}
                onClick={() => stop(m)}
              >
                Stop reading
              </button>
            </div>
          ))
        ) : (
          <div className="muted small">Not connected.</div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!gmailOn ? (
            <button
              type="button"
              className="btn primary"
              disabled={!me || !!busy}
              onClick={() => connect("google")}
            >
              {busy === "google" ? "Waiting for Google…" : "Connect Gmail"}
            </button>
          ) : null}
          {!outlookOn ? (
            <button
              type="button"
              className="btn primary"
              disabled={!me || !!busy}
              onClick={() => connect("microsoft")}
            >
              {busy === "microsoft" ? "Waiting for Microsoft…" : "Connect Outlook"}
            </button>
          ) : null}
        </div>
        {!gmailOn ? (
          <div className="muted small">
            Gmail and Google Workspace: until Google finishes reviewing
            BossDrop, Google shows a &ldquo;Google hasn&apos;t verified this
            app&rdquo; screen. Choose Advanced, then continue to BossDrop. A
            Google Workspace admin can mark BossDrop as trusted to skip it.
          </div>
        ) : null}
        {!outlookOn ? (
          <div className="muted small">
            Outlook and Microsoft 365: if your organization requires it, an
            admin approves BossDrop once.
          </div>
        ) : null}
      </div>

      {canManageWorkspace && others.length ? (
        <div className="panelSub" style={{ margin: 0, padding: 12, display: "grid", gap: 8 }}>
          <strong>Other connected mailboxes</strong>
          {others.map((m) => (
            <div
              key={`${m.provider}:${m.email}`}
              style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
            >
              <span className={`chip ${m.error ? "warn" : "ok"}`}>
                {PROVIDER_LABEL[m.provider] || m.provider}
              </span>
              <span style={{ fontWeight: 600 }}>{m.email}</span>
              <span className="muted small" style={{ flex: "1 1 220px" }}>
                {readLine(m)}
              </span>
              <button
                type="button"
                className="btn"
                disabled={!!busy}
                onClick={() => stop(m)}
              >
                Stop reading
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="muted small">
        Another email provider? CC or forward the thread to{" "}
        <strong>ai@bossdrop.ai</strong> and BossDrop reads it.
      </div>
    </div>
  );
}
